import React, { useState, useEffect, useCallback } from "react";
import { FaClock, FaSignInAlt, FaCheckCircle, FaUserTie } from "react-icons/fa";
import { useTheme, getT, getBrandColors} from "./ThemeContext";
import PageHeaderIcon from "./components/PageHeaderIcon";
import API_BASE from "./apiConfig";
import { useSort } from "./hooks/useSort";
import { useDateTimeFormat } from "./utils/formatters";

const BASE = API_BASE;
const FONT = "'Segoe UI', system-ui, -apple-system, sans-serif";
const BLUR = "blur(18px)";

function isoLocal(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}

function makeS(T) {
  return {
    card:  { background: T.glass, backdropFilter: BLUR, WebkitBackdropFilter: BLUR, border: T.glassBdr, borderRadius: 18, boxShadow: T.glassSh, padding: "28px 32px", boxSizing: "border-box" },
    inp:   { padding: "12px 16px", borderRadius: 10, border: `1.5px solid ${T.inputBdr}`, background: T.inputBg, color: T.inputText, fontSize: 20, fontFamily: FONT, outline: "none", fontWeight: 700 },
    yBtn:  { padding: "14px 32px", border: "none", borderRadius: 12, fontSize: 16, fontWeight: 700, cursor: "pointer", color: getBrandColors().blueDark, background: "linear-gradient(135deg,#ffd700,#fff3a0)", boxShadow: "0 4px 14px rgba(255,200,0,0.32)", display: "flex", alignItems: "center", gap: 10, fontFamily: FONT },
    lbl:   { fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.06em", color: T.textLabel, fontFamily: FONT, marginBottom: 8, display: "block" },
  };
}

// ─── Today Clock-In Card ──────────────────────────────────────────────────────
function TodayCard({ coachUser, T, S }) {
  const { fmtTime } = useDateTimeFormat();
  const today      = isoLocal(new Date());
  const todayLabel = new Date().toLocaleDateString("en-AU", { weekday:"long", day:"numeric", month:"long", year:"numeric" });
  const coachId    = coachUser?.user_id;

  const [log,     setLog]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [clockin, setClockin] = useState("");
  const [toast,   setToast]   = useState(null);

  const load = useCallback(() => {
    if (!coachId) return;
    setLoading(true);
    fetch(`${BASE}/coach-pay/clockin/today?coach_id=${coachId}`)
      .then(r => r.json())
      .then(d => {
        setLog(d.log || null);
        if (!d.log) {
          const now = new Date();
          setClockin(`${String(now.getHours()).padStart(2,"0")}:${String(now.getMinutes()).padStart(2,"0")}`);
        } else {
          setClockin(d.log.clockin_time);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [coachId]);

  useEffect(() => { load(); }, [load]);

  const showToast = (ok, msg) => { setToast({ ok, msg }); setTimeout(() => setToast(null), 4000); };

  const handleClockIn = async () => {
    if (!clockin) { showToast(false, "Please set your clock-in time"); return; }
    setSaving(true);
    try {
      const r = await fetch(`${BASE}/coach-pay/clockin`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ coach_id: coachId, log_date: today, clockin_time: clockin }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "Save failed");
      const data = await r.json();
      showToast(true, `Clocked in at ${clockin} — group hours calculated automatically`);
      load();
    } catch (e) { showToast(false, e.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ ...S.card, marginBottom: 24 }}>
      <div style={{ fontSize: 13, color: T.textMuted, fontFamily: FONT, marginBottom: 20 }}>{todayLabel}</div>

      {toast && (
        <div style={{ marginBottom: 20, padding: "12px 16px", borderRadius: 10, fontSize: 14, fontWeight: 600, fontFamily: FONT,
          background: toast.ok ? "rgba(52,211,153,0.12)" : "rgba(248,113,113,0.12)",
          color: toast.ok ? getBrandColors().green : getBrandColors().red,
          border: toast.ok ? "1px solid rgba(52,211,153,0.3)" : "1px solid rgba(248,113,113,0.3)" }}>
          {toast.msg}
        </div>
      )}

      {loading ? (
        <div style={{ color: T.textMuted, fontFamily: FONT, fontSize: 14 }}>Loading…</div>
      ) : log ? (
        /* Already clocked in — show status */
        <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          <div style={{ width: 52, height: 52, borderRadius: 14, background: "rgba(16,185,129,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <FaCheckCircle size={24} color="#10b981" />
          </div>
          <div>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#10b981", fontFamily: FONT }}>
              Clocked In — {fmtTime(log.clockin_time)}
            </div>
            <div style={{ fontSize: 14, color: T.textMuted, fontFamily: FONT, marginTop: 4 }}>
              Group hours calculated from session start · Clock-out set automatically
            </div>
          </div>
        </div>
      ) : (
        /* Clock-in form */
        <div style={{ display: "flex", alignItems: "flex-end", gap: 20, flexWrap: "wrap" }}>
          <div>
            <label style={S.lbl}>
              <FaSignInAlt size={10} style={{ marginRight: 5 }} />Clock-In Time
            </label>
            <input type="time" value={clockin} onChange={e => setClockin(e.target.value)}
              style={{ ...S.inp, width: 160 }} />
          </div>
          <button style={S.yBtn} onClick={handleClockIn} disabled={saving}>
            <FaSignInAlt size={16} />
            {saving ? "Saving…" : "Clock In"}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Weekly Log Table ─────────────────────────────────────────────────────────
function WeekLog({ coachUser, T, S }) {
  const { fmtDate } = useDateTimeFormat();
  const [logs,    setLogs]    = useState([]);
  const [loading, setLoading] = useState(true);
  const coachId = coachUser?.user_id;

  useEffect(() => {
    if (!coachId) return;
    fetch(`${BASE}/coach-pay/clockin/week?coach_id=${coachId}`)
      .then(r => r.json())
      .then(d => { setLogs(d.logs || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [coachId]);

  const logsWithTotal = logs.map(l => ({ ...l, total_hours: l.group_hours + l.oto_hours }));
  const { sorted: sortedLogs, toggleSort, sortArrow } = useSort(logsWithTotal, "log_date", "desc");

  if (loading) return <div style={{ color: T.textMuted, fontFamily: FONT, fontSize: 14 }}>Loading…</div>;
  if (!logs.length) return (
    <div style={{ ...S.card, textAlign: "center", color: T.textMuted, fontFamily: FONT, fontSize: 14, padding: 40 }}>
      No clock-in records in the last 7 days.
    </div>
  );

  const th = { padding: "12px 16px", fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.06em", color: T.textLabel, fontFamily: FONT, textAlign: "left", whiteSpace: "nowrap" };
  const td = (extra) => ({ padding: "13px 16px", fontSize: 14, fontWeight: 600, color: T.text, fontFamily: FONT, borderTop: `1px solid ${T.divider}`, ...extra });
  const thSort = (extra) => ({ ...th, cursor: "pointer", userSelect: "none", ...extra });

  return (
    <div style={S.card}>
      <div style={{ fontSize: 15, fontWeight: 800, color: T.text, fontFamily: FONT, marginBottom: 16 }}>Last 7 Days</div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={thSort()} onClick={() => toggleSort("log_date")}>Date{sortArrow("log_date")}</th>
              <th style={thSort()} onClick={() => toggleSort("clockin_time")}>Clock In{sortArrow("clockin_time")}</th>
              <th style={thSort()} onClick={() => toggleSort("group_session_start")}>Session Window{sortArrow("group_session_start")}</th>
              <th style={thSort()} onClick={() => toggleSort("effective_group_start")}>Eff. Start{sortArrow("effective_group_start")}</th>
              <th style={thSort()} onClick={() => toggleSort("group_hours")}>Group Hrs{sortArrow("group_hours")}</th>
              <th style={thSort()} onClick={() => toggleSort("oto_hours")}>1-on-1 Hrs{sortArrow("oto_hours")}</th>
              <th style={thSort()} onClick={() => toggleSort("total_hours")}>Total{sortArrow("total_hours")}</th>
            </tr>
          </thead>
          <tbody>
            {sortedLogs.map(l => {
              const late = l.clockin_time > l.group_session_start;
              const total = (l.group_hours + l.oto_hours).toFixed(2);
              return (
                <tr key={l.id}>
                  <td style={td()}>{fmtDate(l.log_date)}</td>
                  <td style={td()}>{l.clockin_time}</td>
                  <td style={td({ color: T.textMuted })}>{l.group_session_start} – {l.group_session_end}</td>
                  <td style={td({ color: late ? "#f59e0b" : "#10b981", fontWeight: 800 })}>
                    {l.effective_group_start}
                    {late && <span style={{ fontSize: 10, marginLeft: 5, opacity: 0.8 }}>late</span>}
                  </td>
                  <td style={td({ color: "#6366f1", fontWeight: 800 })}>{l.group_hours.toFixed(2)}</td>
                  <td style={td({ color: "#0ea5e9", fontWeight: 800 })}>{l.oto_hours.toFixed(2)}</td>
                  <td style={td({ fontWeight: 800 })}>{total}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Main Export ──────────────────────────────────────────────────────────────
export default function CoachClockIn({ currentUser }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const S = makeS(T);

  const role    = currentUser?.user_role;
  const isAdmin = role === "Admin" || currentUser?.is_superadmin || role === "Super Admin";

  const [selectedCoach, setSelectedCoach] = useState(isAdmin ? null : currentUser);
  const [coaches,       setCoaches]       = useState([]);

  useEffect(() => {
    if (!isAdmin) return;
    fetch(`${BASE}/coach-pay/rates`)
      .then(r => r.json())
      .then(d => {
        const list = d.coaches || [];
        setCoaches(list);
        if (list.length) setSelectedCoach({ user_id: list[0].coach_id, full_name: list[0].coach_name });
      })
      .catch(() => {});
  }, [isAdmin]);

  return (
    <div style={{ padding: "28px 32px", maxWidth: 860, margin: "0 auto" }}>
      {/* Page header */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 28 }}>
        <PageHeaderIcon icon={<FaClock size={20} color={getBrandColors().blueDark} />} />
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, color: T.text, fontFamily: FONT }}>Coach Clock-In</div>
          <div style={{ fontSize: 13, color: T.textMuted, fontFamily: FONT, marginTop: 2 }}>
            Record your start time — group coaching hours are calculated automatically from the session schedule
          </div>
        </div>
      </div>

      {/* Admin: coach selector */}
      {isAdmin && coaches.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <label style={S.lbl}><FaUserTie size={10} style={{ marginRight: 4 }} />Viewing Coach</label>
          <select value={selectedCoach?.user_id || ""}
            onChange={e => {
              const c = coaches.find(x => x.coach_id === Number(e.target.value));
              if (c) setSelectedCoach({ user_id: c.coach_id, full_name: c.coach_name });
            }}
            style={{ ...S.inp, width: 260, fontSize: 15 }}>
            {coaches.map(c => <option key={c.coach_id} value={c.coach_id}>{c.coach_name}</option>)}
          </select>
        </div>
      )}

      {selectedCoach ? (
        <>
          <TodayCard coachUser={selectedCoach} T={T} S={S} />
          <WeekLog   coachUser={selectedCoach} T={T} S={S} />
        </>
      ) : (
        <div style={{ ...S.card, textAlign: "center", color: T.textMuted, fontFamily: FONT, fontSize: 14, padding: 40 }}>
          {isAdmin ? "No active coaches found." : "Loading…"}
        </div>
      )}
    </div>
  );
}
